<template>
    <hox-modal data-testid="create-campaign__modal" @close="close">
        <hox-loading-layer v-if="isLoading" />

        <template #header>Create campaign</template>

        <CampaignForm
            ref="form"
            :sections="sections"
            :disable-wpp-project-id="isWppLogin"
            :initial-form-data="initialFormData"
            :has-creative-x-enabled="hasCreativeXEnabled"
            :client-creative-x-secret-id="clientCreativeXSecretId"
            :has-satalia-enabled="hasSataliaEnabled"
            @hasValidationErrorsUpdate="hasValidationErrors = $event"
            @submit="addCampaign"
        />

        <p v-for="error in mutationErrors" :key="error" class="error-wrapper">Error: {{ error }}</p>

        <template #footer>
            <Button
                :disabled="hasValidationErrors"
                type="primary"
                data-testid="create-campaign__submit"
                @click="submit"
            >
                Create
            </Button>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { ApolloError } from "@apollo/client/errors";

import type { CampaignSection } from "@/types/types";
import CampaignForm, { type CampaignFormData } from "@/components/CampaignForm.vue";
import addCampaignMutation from "@/apollo/mutations/AddCampaign.gql";
import { AuthGetters } from "@/store/modules/auth";
import { campaignDefaultLanguage } from "@/store/modules/campaign";
import getDefaultCreativeXCampaignSettings from "@/utils/CreativeX/getDefaultCreativeXCampaignSettings";
import getDefaultSataliaCampaignSettings from "@/utils/Satalia/getDefaultSataliaCampaignSettings";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

export default defineComponent({
    components: {
        CampaignForm
    },

    props: {
        sections: {
            required: true,
            type: Array as PropType<CampaignSection[]>
        },

        hasCreativeXEnabled: {
            type: Boolean,
            default: false
        },

        clientCreativeXSecretId: {
            type: String,
            required: false
        },

        hasSataliaEnabled: {
            type: Boolean,
            default: false
        }
    },

    emits: ["campaignCreated", "close"],

    setup() {
        const ga4 = useGA4Tracking();

        return {
            ga4
        };
    },

    data() {
        return {
            hasValidationErrors: true,
            isLoading: false,
            mutationErrors: [] as string[],
            initialFormData: {} as CampaignFormData,
            isWppLogin: false
        };
    },

    computed: {
        clientId() {
            return this.$route.params.clientId;
        }
    },

    mounted() {
        this.ga4.trackFeatureEvent({
            featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
            featureCategory: GA4.FEATURE_CATEGORIES.CAMPAIGN,
            featureContext: GA4.FEATURE_CONTEXTS.ADD_CAMPAIGN
        });

        this.isWppLogin = this.$store.getters[AuthGetters.isWppLogin];
        const wppProjectId = this.$store.state.auth?.wppOsContext?.project?.id;

        if (this.isWppLogin && wppProjectId) {
            this.initialFormData.wppProjectId = wppProjectId;
        }

        if (this.hasCreativeXEnabled) {
            this.initialFormData.creativeXPlatformSettings = getDefaultCreativeXCampaignSettings();
        }

        if (this.hasSataliaEnabled) {
            this.initialFormData.sataliaPlatformSettings = getDefaultSataliaCampaignSettings();
        }
    },

    methods: {
        close() {
            this.ga4.trackFeatureEvent({
                featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                featureCategory: GA4.FEATURE_CATEGORIES.CAMPAIGN,
                featureContext: GA4.FEATURE_CONTEXTS.ADD_CAMPAIGN
            });
            this.$emit("close");
        },

        submit() {
            (this.$refs.form as HTMLFormElement).submit();
        },

        async addCampaign(formData: CampaignFormData) {
            if (this.isLoading || this.hasValidationErrors) {
                return;
            }

            this.isLoading = true;
            this.mutationErrors = [];

            try {
                await this.$apollo.mutate({
                    mutation: addCampaignMutation,
                    variables: {
                        clientId: this.clientId,
                        campaign: {
                            name: formData.name,
                            campaignType: formData.campaignType,
                            jiraTicketUrl: formData.jiraTicketUrl,
                            languages: [campaignDefaultLanguage],
                            sectionId: formData.sectionId === "none" ? null : formData.sectionId,
                            toneOfVoice: formData.toneOfVoice,
                            wppProjectId: formData.wppProjectId,
                            ...(this.hasCreativeXEnabled && {
                                creativeXPlatformSettings: {
                                    ...getDefaultCreativeXCampaignSettings(),
                                    ...formData.creativeXPlatformSettings
                                }
                            }),
                            opendcPlatformSettings: formData.opendcPlatformSettings,
                            ...(this.hasSataliaEnabled && {
                                sataliaPlatformSettings: {
                                    ...getDefaultSataliaCampaignSettings(),
                                    ...formData.sataliaPlatformSettings
                                }
                            }),
                            wppTenantId: this.$store.state.auth?.wppOsContext?.tenant?.id,
                            wppWorkspaceId: this.$store.state.auth?.wppOsContext?.additional?.workspaceId,
                            wppDevhubAppVersionId:
                                this.$store.state.auth?.wppOsContext?.appInstance?.devhubAppVersionId,
                            wppAzId: this.$store.state.auth?.wppOsContext?.hierarchy?.azId
                        }
                    }
                });

                this.$snackbar.success("Campaign created");
                this.$emit("campaignCreated");
                this.close();
            } catch (error) {
                const graphQLErrors = (error as ApolloError)?.graphQLErrors || [];
                this.mutationErrors = graphQLErrors.map(({ message }) => message);
            } finally {
                this.isLoading = false;
            }
        }
    }
});
</script>

<style scoped lang="css">
.error-wrapper {
    color: red;
    margin-top: 12px;
}
</style>
